import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.js';
import { disablePush, enablePush, getPushStatus, sendTestPush, type PushStatus } from '../lib/push.js';
import { resetServiceWorker } from '../lib/sw-register.js';
import type { Sex } from '../lib/api.js';
import { useEscape } from '../lib/useEscape.js';
import { entriesApi } from '../lib/api.js';
import {
  MAX_BYTES,
  parseCsv,
  templateCsv,
  toCsv,
  type CsvReport,
  type CsvRow,
} from '../lib/csv.js';
import * as fmt from '../lib/format.js';

interface Props {
  onClose: () => void;
  /** Journées déjà saisies : sert à annoncer combien seront remplacées. */
  existingDays: Set<string>;
  /** Journées renseignées, pour l'export. */
  recorded: CsvRow[];
  onImported: () => void;
}

/** Déclenche un téléchargement sans passer par le serveur. */
function download(name: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/** Accord simple : au-delà de un, tout prend un s. */
const plural = (count: number): string => (count > 1 ? 's' : '');

const SEXES: Sex[] = ['female', 'male', 'other'];
const SEX_LABELS: Record<Sex, string> = { female: 'Femme', male: 'Homme', other: 'Autre' };

/** Champ vide = effacement explicite, d'où `null` plutôt que `undefined`. */
function toNumber(text: string): number | null {
  const value = Number.parseFloat(text.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

const asText = (value: number | null | undefined): string => (value == null ? '' : String(value));

const PUSH_LABELS: Record<PushStatus, string> = {
  unsupported: 'Ce navigateur ne gère pas les notifications push.',
  denied: 'Notifications bloquées. À réautoriser dans les réglages du site, côté navigateur.',
  disabled: 'Le serveur n’a pas de clés VAPID configurées.',
  stalled:
    'Le service worker n’est pas actif sur cet appareil : sans lui, aucune notification ne peut arriver. Une réinitialisation le réinstalle.',
  off: 'Aucun rappel ne partira vers cet appareil.',
  on: 'Cet appareil recevra les rappels.',
};

export function SettingsPanel({ onClose, existingDays, recorded, onImported }: Props) {
  const { user, updateProfile, logout } = useAuth();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // L'import se fait en deux temps : on lit et on rapporte, puis l'utilisateur
  // confirme. Écraser des journées déjà saisies est la seule chose vraiment
  // irréversible ici, elle mérite d'être annoncée avant plutôt que constatée
  // après.
  const [pending, setPending] = useState<CsvReport | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const filePicker = useRef<HTMLInputElement | null>(null);

  // Brouillon local plutot qu'ecriture a chaque frappe. Il est valide au
  // `blur` *et* a la fermeture : s'en remettre au seul `blur` perdait la
  // saisie quand le panneau se fermait par Echap ou par le fond.
  //
  // Les champs eux-memes ne sont jamais desactives pendant un enregistrement :
  // l'ecriture du champ precedent est encore en vol quand l'utilisateur passe
  // au suivant, et un `disabled` avalerait sa frappe.
  const [draft, setDraft] = useState({
    birthYear: asText(user?.birthYear),
    heightCm: asText(user?.heightCm),
  });

  useEffect(() => {
    void getPushStatus()
      .then(setStatus)
      .catch(() => setStatus('unsupported'));
  }, []);

  /** N'ecrit que les champs qui different reellement de ce qui est enregistre. */
  function commit() {
    if (!user) return;
    const birthYear = toNumber(draft.birthYear);
    const heightCm = toNumber(draft.heightCm);
    const patch: { birthYear?: number | null; heightCm?: number | null } = {};
    if (birthYear !== user.birthYear) patch.birthYear = birthYear;
    if (heightCm !== user.heightCm) patch.heightCm = heightCm;
    if (Object.keys(patch).length > 0) void run(() => updateProfile(patch));
  }

  function close() {
    commit();
    onClose();
  }

  useEscape(close);

  if (!user) return null;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Opération impossible');
    } finally {
      setBusy(false);
    }
  }

  const subscribable = status === 'on' || status === 'off';

  async function readFile(file: File) {
    setImportError(null);
    setPending(null);
    setMessage(null);
    if (file.size > MAX_BYTES) {
      setImportError(`Fichier de ${Math.round(file.size / 1024)} ko : la limite est de 1 Mo.`);
      return;
    }
    try {
      setPending(parseCsv(await file.text()));
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Fichier illisible.');
    }
  }

  async function applyImport(report: CsvReport) {
    await run(async () => {
      const result = await entriesApi.import(report.rows);
      setPending(null);
      setMessage(`${result.saved} journée${plural(result.saved)} importée${plural(result.saved)}.`);
      onImported();
    });
  }

  const replaced = pending ? pending.rows.filter((row) => existingDays.has(row.day)).length : 0;

  return (
    <>
      <button type="button" className="overlay" aria-label="Fermer" onClick={close} />
      <aside className="panel settings" role="dialog" aria-label="Réglages">
        <div className="panel__grab" aria-hidden="true" />

        <header className="panel__head">
          <div>
            <div className="eyebrow">Réglages</div>
            <h2 className="panel__title" style={{ textTransform: 'none' }}>
              {user.displayName ?? user.email}
            </h2>
            <p className="panel__rel">{user.email}</p>
          </div>
          <button type="button" className="btn--icon" onClick={close} aria-label="Fermer">
            ✕
          </button>
        </header>

        <section className="settings__section">
          <div className="stat__label">Rappel quotidien</div>

          <div className="settings__row">
            <div>
              <div style={{ fontSize: 13 }}>Me rappeler de saisir</div>
              <p className="hint">
                Envoyé seulement si le poids du jour n’est pas encore renseigné.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              className="switch"
              aria-checked={user.reminderEnabled}
              aria-label="Activer le rappel quotidien"
              disabled={busy}
              onClick={() => void run(() => updateProfile({ reminderEnabled: !user.reminderEnabled }))}
            />
          </div>

          <div className="settings__row">
            <div style={{ fontSize: 13 }}>Heure</div>
            <input
              type="time"
              value={user.reminderTime}
              disabled={busy || !user.reminderEnabled}
              onChange={(event) => void run(() => updateProfile({ reminderTime: event.target.value }))}
            />
          </div>

          <p className="hint" style={{ marginTop: 12 }}>
            {status === null ? 'Vérification des notifications…' : PUSH_LABELS[status]}
          </p>

          {status === 'stalled' ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() => void run(resetServiceWorker)}
              >
                Réinstaller le service worker
              </button>
            </div>
          ) : null}

          {subscribable ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    setStatus(status === 'on' ? await disablePush() : await enablePush());
                  })
                }
              >
                {status === 'on' ? 'Désabonner cet appareil' : 'Autoriser sur cet appareil'}
              </button>
              {status === 'on' ? (
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const result = await sendTestPush();
                      const error = result.errors[0];
                      setMessage(
                        result.sent > 0
                          ? `Envoyé à ${result.sent} appareil(s).`
                          : error
                            ? `Refusé par le service push (${error.status ?? 'réseau'}) : ${error.message}`
                            : 'Aucun appareil abonné n’a répondu.',
                      );
                    })
                  }
                >
                  Test
                </button>
              ) : null}
            </div>
          ) : null}

          {message ? (
            <p className="hint" role="status" style={{ marginTop: 10 }}>
              {message}
            </p>
          ) : null}
        </section>

        <section className="settings__section">
          <div className="stat__label">Morphologie</div>
          <p className="hint" style={{ marginBottom: 12 }}>
            Sert uniquement à estimer une dépense de repos tant que le bilan énergétique n’a pas
            assez de saisies. Tout est facultatif ; sans taille ni année de naissance, l’estimation
            retombe sur un forfait au kilo, à ±15 % près.
          </p>

          <div className="settings__row">
            <div style={{ fontSize: 13 }}>Sexe</div>
            <div className="segmented" role="group" aria-label="Sexe">
              {SEXES.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={busy}
                  aria-pressed={user.sex === option}
                  onClick={() =>
                    void run(() => updateProfile({ sex: user.sex === option ? null : option }))
                  }
                >
                  {SEX_LABELS[option]}
                </button>
              ))}
            </div>
          </div>

          <div className="settings__row">
            <div style={{ fontSize: 13 }}>Année de naissance</div>
            <input
              className="settings__number"
              type="number"
              min={1900}
              max={new Date().getFullYear()}
              inputMode="numeric"
              placeholder="—"
              aria-label="Année de naissance"
              value={draft.birthYear}
              onChange={(event) =>
                setDraft((current) => ({ ...current, birthYear: event.target.value }))
              }
              onBlur={commit}
            />
          </div>

          <div className="settings__row">
            <div style={{ fontSize: 13 }}>Taille</div>
            <div className="inputgroup inputgroup--tight">
              <input
                className="settings__number"
                type="number"
                min={81}
                max={259}
                step={0.5}
                inputMode="decimal"
                placeholder="—"
                aria-label="Taille en centimètres"
                value={draft.heightCm}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, heightCm: event.target.value }))
                }
                onBlur={commit}
              />
              <span>cm</span>
            </div>
          </div>
        </section>

        <section className="settings__section">
          <div className="stat__label">Repère initial</div>
          <div className="settings__row">
            <div>
              <div style={{ fontSize: 13 }}>Poids de départ</div>
              <p className="hint">Sert de point de comparaison, jamais recalculé.</p>
            </div>
            <b className="mono">{user.startWeight == null ? '—' : `${fmt.weight(user.startWeight)} kg`}</b>
          </div>
          <div className="settings__row">
            <div style={{ fontSize: 13 }}>Fuseau des rappels</div>
            <b className="mono" style={{ fontSize: 12 }}>
              {user.timezone}
            </b>
          </div>
        </section>

        <section className="settings__section">
          <div className="stat__label">Import CSV</div>
          <p className="hint" style={{ marginBottom: 10 }}>
            Pour rattraper plusieurs journées d’un coup, ou sortir ses données. Une journée déjà
            saisie est remplacée à l’import ; l’export se relit tel quel.
          </p>

          <div className="settings__buttons">
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => download('ephemeris-modele.csv', templateCsv())}
            >
              Modèle
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              disabled={recorded.length === 0}
              onClick={() =>
                download(`ephemeris-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(recorded))
              }
            >
              Exporter
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              disabled={busy}
              onClick={() => filePicker.current?.click()}
            >
              Importer un fichier
            </button>
            <input
              ref={filePicker}
              type="file"
              accept=".csv,text/csv,text/plain"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Remet le champ à zéro : sans cela, réimporter le même fichier
                // après correction ne déclencherait aucun événement.
                event.target.value = '';
                if (file) void readFile(file);
              }}
            />
          </div>

          {importError ? (
            <p className="error" role="alert" style={{ marginTop: 10 }}>
              {importError}
            </p>
          ) : null}

          {pending ? (
            <div className="settings__confirm" role="status">
              <div>
                <b>
                  {pending.rows.length} journée{plural(pending.rows.length)} lisible
                  {plural(pending.rows.length)}
                </b>
                {replaced > 0
                  ? `, dont ${replaced} qui ${replaced > 1 ? 'remplaceront' : 'remplacera'} une saisie existante`
                  : ''}
                .
                {pending.empty > 0
                  ? ` ${pending.empty} ligne${plural(pending.empty)} sans mesure ignorée${plural(pending.empty)}.`
                  : ''}
              </div>

              {pending.problems.length > 0 ? (
                <ul className="settings__problems">
                  {pending.problems.slice(0, 5).map((problem) => (
                    <li key={`${problem.line}-${problem.reason}`}>
                      ligne {problem.line} : {problem.reason}
                    </li>
                  ))}
                  {pending.problems.length > 5 ? (
                    <li>
                      et {pending.problems.length - 5} autre
                      {plural(pending.problems.length - 5)} ligne
                      {plural(pending.problems.length - 5)} ignorée
                      {plural(pending.problems.length - 5)}
                    </li>
                  ) : null}
                </ul>
              ) : null}

              <div className="settings__buttons">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy || pending.rows.length === 0}
                  onClick={() => void applyImport(pending)}
                >
                  Importer
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  disabled={busy}
                  onClick={() => setPending(null)}
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <div className="panel__actions">
          <button type="button" className="btn btn--ghost" style={{ flex: 1 }} onClick={() => void logout()}>
            Se déconnecter
          </button>
        </div>
      </aside>
    </>
  );
}
